import {
  computed,
  ElementRef,
  effect,
  inject,
  OutputEmitterRef,
  Signal,
  WritableSignal,
} from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { equals, Store } from '@softline/core';
import { DateParser, ModalStore, SOFTLINE_FEATURE_MODAL } from '@softline/ui-core';
import { DateRangeInputPickerTrigger } from './date-range-input.types';

// legacy modal DateRange shape (nullable endpoints), not the barrel's DateRange
interface DateRange {
  from: string | null;
  to: string | null;
}

export abstract class DateRangeFieldInput implements ControlValueAccessor {
  private readonly store = inject(Store);
  private readonly dateParser = inject<DateParser<string>>(DateParser);
  private hasPendingPointerFocus = false;

  private onChange: (value: string | null) => void = () => void 0;
  onTouch: () => void = () => void 0;

  abstract textInput: WritableSignal<string | null>;
  abstract disabled: WritableSignal<boolean>;
  abstract value: WritableSignal<string | null>;
  abstract valueChange: OutputEmitterRef<string | null>;
  abstract blur: OutputEmitterRef<void>;
  abstract pickerOpen: OutputEmitterRef<'from' | 'to'>;

  abstract valueInput: Signal<string | null>;
  abstract disabledInput: Signal<boolean>;
  abstract readonly: Signal<boolean>;
  abstract placeholder: Signal<string>;
  abstract inputId: Signal<string | null>;
  abstract changeTrigger: Signal<'blur' | 'input'>;
  abstract format: Signal<string>;
  abstract otherControl: Signal<DateRangeFieldInput | null>;
  abstract pickerTrigger: Signal<DateRangeInputPickerTrigger>;
  protected abstract inputElementRef: Signal<
    ElementRef<HTMLInputElement> | undefined
  >;
  protected abstract field: 'from' | 'to';

  readonly canOpenPicker = computed(() => !this.readonly() && !this.disabled());

  private readonly valueInputEffect = effect(() => {
    this.value.set(this.valueInput() ?? null);
  });

  private readonly disabledInputEffect = effect(() => {
    this.disabled.set(this.disabledInput() ?? false);
  });

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled.set(disabled);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  writeValue(obj: any): void {
    if (!obj) {
      this.value.set(null);
      this.textInput.set(null);
      return;
    }

    const value = this.dateParser.parse(obj);
    if (!value) return;

    this.value.set(value);
    if (value !== obj) {
      this.onChange(value);
      this.valueChange.emit(value);
    }
  }

  onValueChange(value: string | null): void {
    if (this.changeTrigger() === 'blur') {
      this.setValue(value, true);
    }
    if (this.value()) {
      this.textInput.set(null);
    }
  }

  onTextInput(value: string | null): void {
    this.textInput.set(value);
    if (this.changeTrigger() === 'input') {
      this.setValue(value, true);
    }
  }

  onInputPointerDown(): void {
    this.hasPendingPointerFocus = true;
  }

  async onInputFocus(): Promise<void> {
    if (this.pickerTrigger() !== 'manual') return;
    if (this.hasPendingPointerFocus) return;
    await this.openDateRangePickerModal();
  }

  async onInputClick(): Promise<void> {
    this.hasPendingPointerFocus = false;
    if (this.pickerTrigger() !== 'manual') return;
    await this.openDateRangePickerModal();
  }

  onInputBlur(): void {
    this.hasPendingPointerFocus = false;
    this.onTouch();
    this.blur.emit();
  }

  setValue(value: string | null, parse: boolean): void {
    const prevValue = this.value();
    const nextValue = !value ? null : parse ? this.dateParser.parse(value) ?? null : value;

    if (equals(nextValue, prevValue)) return;

    this.value.set(nextValue);
    this.onChange(nextValue);
    this.valueChange.emit(nextValue);
  }

  async focusAndOpenPicker(): Promise<void> {
    if (!this.canOpenPicker()) return;

    this.hasPendingPointerFocus = true;
    this.inputElementRef()?.nativeElement.focus();
    this.hasPendingPointerFocus = false;

    await this.openDateRangePickerModal();
  }

  async openDateRangePickerModal(): Promise<void> {
    if (!this.canOpenPicker()) return;

    this.onTouch();

    if (this.pickerTrigger() === 'manual') {
      this.pickerOpen.emit(this.field);
      return;
    }

    const value: DateRange = { from: null, to: null };
    value[this.field] = this.value();

    const otherControl = this.otherControl();
    const otherField = this.field === 'from' ? 'to' : 'from';
    if (otherControl) {
      value[otherField] = otherControl.value();
    }

    const result = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.dateRange,
      { value, dismiss: true, startField: this.field }
    );

    if (!result || result === 'DISMISSED') return;

    this.setValue(result[this.field], false);
    this.setOtherValue(result[otherField], false);
  }

  private setOtherValue(value: string | null, parse: boolean): void {
    const otherControl = this.otherControl();
    if (!otherControl) return;

    otherControl.setValue(value, parse);
  }
}
